import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createApiClient } from '@shared/api/client/api-client';
import { createAuthApi } from '@shared/api/lib/auth-api';
import { mapAuthResponseDtoToEntity } from '@entities/auth';
import { useAuth } from '@entities/auth';
import { applyAuthResponse } from '@entities/auth';

import { env } from '@shared/config/env';

interface UseTwoFactorResult {
  isLoading: boolean;
  error: string | null;
  confirmLogin: (code: string) => Promise<{ success: boolean; error?: string }>;
}

export function useTwoFactor(): UseTwoFactorResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation('auth');
  const { login: authLogin } = useAuth();

  const confirmLogin = async (code: string): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);
    setError(null);

    try {
      const apiClient = createApiClient({ baseURL: env.API_BASE_URL });
      const authApi = createAuthApi(apiClient);

      const response = await authApi.loginConfirm({ secondFactor: { method: 'totp', code } });
      const authResponse = mapAuthResponseDtoToEntity(response);
      await applyAuthResponse(authResponse, authLogin);

      return { success: true };
    } catch (err: unknown) {
      const message =
        typeof err === 'object' && err !== null && 'message' in err && typeof err.message === 'string'
          ? err.message
          : '';
      const errorMessage = message || t('errors.verificationFailed');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, error, confirmLogin };
}
